import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type {
  EligibilityResult,
  Position,
  VacancyCandidate,
  VacancyCandidateList,
} from '@shared/types';
import { api, type CandidateOrder } from '../api/client.js';
import type { ExportTable } from '../lib/exportTable.js';
import { PersonCard, levelLabel } from '../pages/PersonCard.js';
import { ExportButtons } from './ExportButtons.js';

/**
 * ผู้เข้าข่ายของอัตราหนึ่ง — แสดงในหน้าต่างรายละเอียดอัตรา
 *
 * ⚠ แสดงทั้งอัตราที่ว่างและอัตราที่มีผู้ครอง (เจ้าของระบบสั่ง เพื่อวางแผนล่วงหน้าว่า
 *   "ถ้าอัตรานี้ว่างลง ใครรับต่อได้") ถ้อยคำทุกที่จึงต้องแยกสองกรณีออกจากกัน
 *   โดยเฉพาะหัวเรื่องของไฟล์ที่ส่งออก ซึ่งหลุดจากบริบทของหน้าจอไปแล้ว
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องแสดงสองชั้น ไม่ใช่รายชื่อเดียว
 * ────────────────────────────────────────────────────────────────
 *
 * สองชั้นนี้มาจากคนละกฎ และมีความหนักแน่นไม่เท่ากัน
 *
 *   ชั้นที่ 1  ผ่านเกณฑ์ระยะเวลาตามมาตรฐานกำหนดตำแหน่งของ ก.พ. — เป็นการ "เลื่อน"
 *             มีเฉพาะระดับที่ระบบมีเอกสาร ตอนนี้มีชุดเดียวคืออำนวยการสูง
 *   ชั้นที่ 2  อยู่สายงานและระดับเดียวกันในหน่วยอื่น — เป็นการ "ย้าย" ระดับเดิม
 *             ใช้ได้ทุกอัตรา ไม่ต้องมีเอกสารเกณฑ์
 *
 * ถ้ายุบเป็นรายชื่อเดียว ผู้ใช้จะแยกไม่ออกว่าคนที่เห็นผ่านเกณฑ์อะไรมา
 * แล้วจะอ่านทั้งก้อนว่า "คนกลุ่มนี้มีคุณสมบัติเท่ากัน" ซึ่งไม่จริง
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ถ้อยคำและการจัดลำดับ — กฎเดียวกับ RotationModal และ Eligibility
 * ────────────────────────────────────────────────────────────────
 *
 * ห้ามเขียนว่าระบบ "แนะนำ" ใคร ห้ามใส่คะแนนหรือเปอร์เซ็นต์ความเหมาะสม
 * และลำดับที่แสดงต้องมีคำกำกับเสมอว่าคือลำดับอาวุโส ไม่ใช่ลำดับความเหมาะสม
 *
 * ────────────────────────────────────────────────────────────────
 *  กดชื่อแล้วกางประวัติออกมาในที่เดิม ไม่ใช่เปิดหน้าต่างซ้อน
 * ────────────────────────────────────────────────────────────────
 *
 * หน้านี้อยู่ในหน้าต่างอัตราอยู่แล้ว ถ้าเปิดหน้าต่างที่สองซ้อนขึ้นมา ผู้ใช้จะปิดผิดใบ
 * แล้วหลุดออกจากอัตราที่กำลังดูอยู่ ทั้งที่ตั้งใจจะกลับมาเทียบคนถัดไป
 *
 * กางได้ทีละคน — เปิดคนใหม่แล้วคนเก่าปิดเอง เพราะการ์ดประวัติสูงหลายร้อยพิกเซล
 * ถ้ากางค้างไว้พร้อมกันสามคน รายชื่อที่เหลือจะถูกดันหายไปจากสายตา
 *
 * ⚠ ต้องแสดง "ระดับปัจจุบัน" คู่กับชื่อตำแหน่งเสมอ
 * ฟิลด์ title ใน dump มักเป็นชื่อสายงาน ไม่ใช่ชื่อตำแหน่งบริหารที่คนนั้นครองอยู่จริง
 * เช่นคนที่ครองตำแหน่งอำนวยการสูงจะขึ้น title ว่า "เจ้าพนักงานธุรการ"
 * ถ้าโชว์แต่ title ผู้ใช้จะอ่านว่าระบบให้เจ้าพนักงานธุรการผ่านเกณฑ์ผู้อำนวยการระดับสูง
 * ซึ่งดูเหมือนบั๊กทันที ทั้งที่ข้อมูลถูก
 */
export function VacancyCandidates({
  pos,
  onCompare,
}: {
  pos: Position;
  /**
   * ผู้ใช้กดเทียบผู้ที่ติ๊กเลือกไว้
   *
   * ⚠ หน้านี้ไม่ได้แสดงผลเทียบเอง — ส่งรหัสขึ้นไปให้หน้าต่างที่ครอบอยู่สลับเนื้อในแทน
   *   เพราะตารางเทียบต้องใช้ความกว้างทั้งหน้าต่าง ถ้าแทรกไว้ในกล่องยุบได้ของชั้นนี้
   *   สามคอลัมน์จะถูกบีบจนอ่านไม่ได้บนจอแคบ (ดูเหตุผลเรื่องหน้าต่างซ้อนที่ PositionDetail)
   *
   * ไม่ส่ง prop นี้มา = ปิดการเลือกทั้งหมด ไม่ใช่ให้ปุ่มกดแล้วไม่เกิดอะไรขึ้น
   */
  onCompare?: (ids: string[]) => void;
}) {
  /** รหัสบุคคลที่กำลังกางประวัติอยู่ — null คือไม่มีใครกาง */
  const [open, setOpen] = useState<string | null>(null);
  /**
   * รหัสบุคคลที่ติ๊กไว้เพื่อเทียบ — เรียงตามลำดับที่ติ๊ก
   *
   * ⚠ ห้ามเรียงใหม่ ลำดับนี้กลายเป็นลำดับคอลัมน์ในตารางเทียบ
   *   ถ้าเรียงตามลำดับในรายชื่อ คอลัมน์แรกจะกลายเป็น "คนที่ระบบจัดให้เป็นที่หนึ่ง"
   *   ซึ่งเป็นการชี้นำที่ไม่มีใครสั่ง
   */
  const [picked, setPicked] = useState<string[]>([]);
  /** ชั้นย้ายแนวราบยุบไว้ตั้งแต่แรก */
  const [lateralOpen, setLateralOpen] = useState(false);
  /** กล่องข้อจำกัดยุบไว้ตั้งแต่แรก — ยังอยู่ครบและยังติดไปกับไฟล์ที่ส่งออกเสมอ */
  const [warnOpen, setWarnOpen] = useState(false);
  /* ⚠ ค่าตั้งต้นคือ "เข้าเกณฑ์มากที่สุด" ตามที่ผู้ใช้สั่ง
     แต่ยังสลับกลับไปดูลำดับอาวุโสได้ เพราะลำดับอาวุโสคือลำดับที่มีฐานตามหลักเกณฑ์ ก.พ.
     ส่วนลำดับ "เข้าเกณฑ์มากที่สุด" เป็นการจัดหน้าจอเพื่อให้อ่านง่าย ไม่ใช่ลำดับตามกฎ */
  /*
   * ⚠ วิธีเรียงเป็นค่าคงที่แล้ว ไม่ใช่ state — ผู้ใช้สั่งเอาปุ่มสลับวิธีเรียงออก
   *
   * ค่าที่ใช้ต้องเป็น 'criteria' ตัวเดียวกับ DEFAULT_ORDER ฝั่งเซิร์ฟเวอร์
   * (services/vacancyCandidates.ts) ถ้าสองฝั่งไม่ตรงกัน รายชื่อที่เห็นจะเรียง
   * คนละแบบกับที่ scopeOf() ใช้ตัดสินขอบเขต โดยไม่มีอะไรฟ้อง
   *
   * ⚠ ลำดับยังมีความหมายอยู่ จึงยังต้องมีประโยคกำกับว่ามันแปลว่าอะไร — ย้ายไปต่อท้าย
   *   บรรทัด "ตรวจตาม…" แล้ว ห้ามตัดทิ้งเพียงเพราะปุ่มเลือกวิธีเรียงหายไป
   */
  const order: CandidateOrder = 'criteria';
  const toggle = (id: string | null) => setOpen((cur) => (cur === id ? null : id));
  /* ติ๊กแล้วติ๊กซ้ำ = เอาออก · ครบเพดานแล้วติ๊กเพิ่ม = ไม่เกิดอะไร (ช่องถูกปิดไว้อยู่แล้ว) */
  const pick = (id: string) =>
    setPicked((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= MAX_COMPARE ? cur : [...cur, id],
    );

  /* ⚠ ค้นด้วย posId ไม่ใช่ posNo — เลขที่ตำแหน่งซ้ำกันได้ทั้งข้ามกรมและในกรมเดียวกัน
     (ในชุดนี้ซ้ำ 174 เลข รวม 512 อัตรา) การค้นด้วยเลขที่จะได้ 404 สำหรับอัตราเหล่านั้น
     ทั้งที่อัตรามีอยู่จริง และอัตราที่ไม่มีเลขที่เลยจะหาผู้เข้าข่ายไม่ได้ทั้งที่หาได้ */
  const q = useQuery({
    queryKey: ['vacancy-candidates', pos.posId, order],
    queryFn: () => api.vacancyCandidates(pos.posId, order),
  });

  if (q.isPending) return <p className="state sm">กำลังหาผู้เข้าข่ายของอัตรานี้…</p>;
  if (q.isError) return <p className="state sm error">{String(q.error)}</p>;

  const d = q.data;
  if (!d) return null;

  /** รายชื่อหลักเป็นกลุ่มแนวราบ — เมื่อไม่มีมาตรฐาน หรือไม่มีใครผ่านเกณฑ์ */
  const primaryLateral = !d.hasStandard || (d.eligibleTotals?.total ?? 0) === 0;

  /**
   * ตารางที่จะส่งออก — รวมสองชั้นไว้ในตารางเดียว แต่มีคอลัมน์ "ที่มา" กำกับทุกแถว
   *
   * ⚠ คอลัมน์ "ที่มา" ตัดออกไม่ได้เด็ดขาด
   *
   * บนหน้าจอ สองชั้นแยกกันด้วยหัวข้อและคำอธิบายยาว ๆ ที่บอกว่าชั้นไหนมาจากกฎอะไร
   * แต่ในไฟล์ที่ถูก copy ไปวางต่อ โครงนั้นหายหมด เหลือแต่รายชื่อเรียงกัน
   * ถ้าไม่มีคอลัมน์นี้ คนอ่านจะเข้าใจว่าทุกคนในไฟล์ผ่านเกณฑ์เดียวกัน ซึ่งไม่จริง —
   * ชั้นแรกคือ "เลื่อน" ตามมาตรฐานกำหนดตำแหน่ง ชั้นสองคือ "ย้าย" ในระดับเดิม
   */
  const buildExport = (): ExportTable => ({
    /* ⚠ หัวเรื่องต้องแยกว่าง/ไม่ว่าง — ไฟล์ที่ส่งออกไปวางต่อไม่มีหน้าจอกำกับแล้ว
       ถ้าอัตราที่ยังมีผู้ครองได้หัวเรื่อง "อัตราว่าง" ติดไป คนอ่านปลายทางจะเข้าใจว่า
       ตำแหน่งนั้นว่างจริง ๆ ซึ่งเป็นข้อมูลผิดที่ระบบเป็นคนพิมพ์ให้เอง */
    title: pos.vacant
      ? `ผู้เข้าข่ายอัตราว่าง ${d.posNo ?? ''} ${d.title}`.trim()
      : `ผู้เข้าข่ายเมื่ออัตราว่างลง ${d.posNo ?? ''} ${d.title}`.trim(),
    subtitle: `${d.title} · ${levelLabel(d.level)} · ${d.unit}${
      pos.vacant
        ? d.vacantMonths !== null
          ? ` · ว่างมาแล้ว ${d.vacantMonths} เดือน`
          : ''
        : ` · ยังไม่ว่าง ผู้ครองปัจจุบัน ${pos.incumbent?.name ?? '—'} · รายชื่อนี้ใช้วางแผนล่วงหน้า`
    }`,
    warnings: d.warnings,
    columns: [
      { key: 'src', label: 'ที่มาของรายชื่อ', width: 34 },
      { key: 'rank', label: 'ลำดับอาวุโสในกลุ่ม', width: 15, numeric: true },
      { key: 'name', label: 'ชื่อ-สกุล', width: 26 },
      { key: 'title', label: 'ตำแหน่งปัจจุบัน', width: 30 },
      { key: 'level', label: 'ระดับปัจจุบัน', width: 18 },
      { key: 'unit', label: 'หน่วยงาน', width: 34 },
      { key: 'extra', label: 'หมายเหตุ', width: 40 },
    ],
    rows: [
      ...(d.eligible ?? []).map((c) => ({
        src: 'ผ่านเกณฑ์ระยะเวลาตามมาตรฐานกำหนดตำแหน่ง (เลื่อนระดับ)',
        rank: c.seniorityRank,
        name: c.name,
        title: c.currentTitle,
        level: levelLabel(c.currentLevel),
        unit: c.unit,
        extra: [
          c.passedPaths.length > 0 ? `ผ่านข้อ ${c.passedPaths.join(', ')}` : '',
          c.pendingManualChecks.length > 0
            ? `ต้องให้เจ้าหน้าที่ยืนยันอีก ${c.pendingManualChecks.length} ข้อ`
            : '',
          c.retiresWithinMonths !== null ? `เกษียณในอีก ${c.retiresWithinMonths} เดือน` : '',
        ]
          .filter(Boolean)
          .join(' · '),
      })),
      ...d.lateral.map((c) => ({
        src: 'สายงานและระดับเดียวกันในหน่วยอื่น (ย้ายระดับเดิม)',
        rank: null,
        name: c.name,
        title: c.title,
        level: levelLabel(d.level),
        unit: c.unit,
        extra: [
          c.sameDepartment ? 'อยู่ส่วนราชการเดียวกัน' : '',
          c.monthsInPosition !== null ? `ครองตำแหน่งเดิม ${c.monthsInPosition} เดือน` : '',
          c.retireInYears !== null && c.retireInYears <= 1 ? 'จะเกษียณภายใน 1 ปี' : '',
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    ],
  });

  /*
   * แถบดาวน์โหลด — วางไว้ "เหนือรายชื่อ" ของชั้นที่ 1 ตามที่ผู้ใช้สั่ง
   * (เดิมอยู่บนสุดของทั้งบล็อก เหนือหัวกล่อง)
   *
   * ⚠ ไฟล์ที่ได้รวมรายชื่อ "ทั้งสองชั้น" ไม่ใช่เฉพาะชั้นที่มันไปวางอยู่
   *   พอย้ายมาแทรกกลางชั้นที่ 1 ความเสี่ยงคือผู้ใช้อ่านว่าดาวน์โหลดเฉพาะชั้นนี้
   *   ประโยคกำกับข้าง ๆ ปุ่มจึงเป็นสิ่งที่ทำให้ย้ายมาตรงนี้ได้ ห้ามตัดทิ้ง
   */
  const tools = (
    <div className="vc-tools">
      <span>ไฟล์ที่ได้รวมรายชื่อทั้งสองชั้นไว้ด้วยกัน พร้อมคอลัมน์บอกที่มาของแต่ละแถว</span>
      <ExportButtons build={buildExport} />
    </div>
  );

  return (
    <>
      {/* ---------- ชั้นที่ 1 ---------- */}
      {/* ⚠ หัวชั้นนี้เคยเป็นสามกล่องซ้อนกัน — หัวข้อขีดเส้นใต้ทึบ · กล่องโน้ตสีฟ้า ·
          กล่องเลือกวิธีเรียงสีเทา — แต่ละใบมีพื้นหลังคนละสี อ่านแล้วเหมือนคนละเรื่องสามเรื่อง
          ทั้งที่เป็นหัวของรายการเดียวกัน ยุบเหลือกล่องเดียว (.vc-head) แล้วเรียงเป็นชั้น
          ชื่อ → ที่มาของเกณฑ์ → ตัวควบคุม → คำกำกับ */}
      <section className="pmodal-sec">
        {primaryLateral ? (
          <>
            {/* ผู้ใช้สั่ง 19 ก.ย. 2569: ทุกอัตราแสดง 5 อันดับแรกเสมอ ที่เหลือให้ค้นหา
                อัตราที่ไม่มีมาตรฐาน (หรือไม่มีใครผ่านเกณฑ์) จึงยกกลุ่มแนวราบขึ้นมาเป็นรายชื่อหลักแทน
                แทนที่จะเหลือแค่ข้อความ "ยังไม่มีเอกสาร" กับกล่องยุบที่ไม่มีใครกด */}
            <LateralTier
              posId={pos.posId}
              d={d}
              open={open}
              onToggle={toggle}
              heading
              note={
                !d.hasStandard
                  ? <>ยังไม่มีเอกสารมาตรฐานกำหนดตำแหน่งของระดับ{levelLabel(d.level) || 'นี้'}ในระบบ จึงตรวจเกณฑ์เลื่อนระดับไม่ได้ — <b>ไม่ได้แปลว่าไม่มีผู้เข้าข่าย</b> รายชื่อด้านล่างคือผู้ที่ย้ายมาได้โดยไม่ต้องเลื่อนระดับ</>
                  : <>ไม่มีผู้ผ่านเกณฑ์ระยะเวลาตาม{d.standardTitle ?? 'มาตรฐานกำหนดตำแหน่ง'} รายชื่อด้านล่างคือผู้ที่ย้ายมาได้โดยไม่ต้องเลื่อนระดับ</>
              }
            />
            {tools}
          </>
        ) : !d.hasStandard ? (
          <>
            <h4>
              ผู้ผ่านเกณฑ์ระยะเวลาตามมาตรฐานกำหนดตำแหน่ง
              {d.eligibleTotals && (
                <span className="vc-count">{d.eligibleTotals.total.toLocaleString('th-TH')} คน</span>
              )}
            </h4>
            {/* ⚠ ต้องเขียนให้ชัดว่า "ระบบไม่มีเกณฑ์" ไม่ใช่ "ไม่มีใครผ่าน"
                สองอย่างนี้คนละเรื่องกัน และการอ่านผิดมีผลต่อการตัดสินใจจริง */}
            <p className="pmodal-note">
              ยังไม่มีเอกสารมาตรฐานกำหนดตำแหน่งของระดับ{levelLabel(d.level) || 'นี้'}ในระบบ
              จึงตรวจเกณฑ์ระยะเวลาให้ไม่ได้ — <b>ไม่ได้แปลว่าไม่มีผู้เข้าข่าย</b>
            </p>
            {/* ⚠ ชั้นนี้ตรวจเกณฑ์ไม่ได้ แต่ชั้นที่ 2 ยังมีรายชื่ออยู่ ปุ่มดาวน์โหลดจึงต้องมี */}
            {tools}
          </>
        ) : (
          <EligibleTier
            pos={pos}
            d={d}
            tools={tools}
            open={open}
            onToggle={toggle}
            picked={picked}
            onPick={onCompare ? pick : undefined}
            onClearPicks={() => setPicked([])}
            onCompare={onCompare}
          />
        )}
      </section>

      {/* ---------- ชั้นที่ 2 ----------
          ⚠ ยุบไว้ตั้งแต่แรกตามที่ผู้ใช้ขอ — ชั้นนี้เป็นทางเลือกรอง (ย้ายระดับเดิม)
            ส่วนคำถามแรกของผู้ใช้คือ "ใครเลื่อนขึ้นมาได้" ซึ่งอยู่ชั้นบน
            หัวข้อยังบอกจำนวนไว้ตอนยุบ กล่องที่ยุบแล้วไม่บอกอะไรเลยคือกล่องที่ไม่มีใครกด */}
      {!primaryLateral && (
      <section className={`pmodal-sec vc-sec${lateralOpen ? ' is-open' : ''}`}>
        <h4>
          <button
            type="button"
            className="vc-secbtn"
            aria-expanded={lateralOpen}
            onClick={() => setLateralOpen((v) => !v)}
          >
            <span className="vc-caret" aria-hidden="true">
              {lateralOpen ? <DownOutlined /> : <RightOutlined />}
            </span>
            ผู้ดำรงตำแหน่งในสายงานและระดับเดียวกัน ณ ส่วนราชการอื่น
            <span className="vc-count">{d.lateralTotals.found} คน</span>
          </button>
        </h4>
        {lateralOpen && <LateralTier posId={pos.posId} d={d} open={open} onToggle={toggle} />}
      </section>
      )}

      {/* ⚠ ยุบไว้ได้ แต่ห้ามเอาออก และห้ามซ่อนจนหาไม่เจอ
          หัวข้อยังบอกจำนวนข้อไว้ตอนยุบ และไฟล์ที่ส่งออกยังพาคำเตือนทุกข้อไปด้วยเสมอ
          (ดู buildExport ข้างบน) สิ่งที่ยุบคือพื้นที่บนจอ ไม่ใช่ตัวคำเตือน */}
      <section className={`pmodal-sec vc-sec${warnOpen ? ' is-open' : ''}`}>
        <h4>
          <button
            type="button"
            className="vc-secbtn"
            aria-expanded={warnOpen}
            onClick={() => setWarnOpen((v) => !v)}
          >
            <span className="vc-caret" aria-hidden="true">
              {warnOpen ? <DownOutlined /> : <RightOutlined />}
            </span>
            ข้อจำกัดของผลลัพธ์ — โปรดอ่านก่อนนำไปใช้
            <span className="vc-count">{d.warnings.length} ข้อ</span>
          </button>
        </h4>
        {warnOpen && (
          <ul className="rotwarn">
            {d.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

/* ================================================================
   ชั้นที่ 1 — ห้าอันดับแรก แล้วค้นหาคนอื่นมาเทียบเอง
   ================================================================ */

/** แสดงกี่คน — "หัวรายชื่อ" ไม่ใช่รายชื่อทั้งหมด */
const FIRST = 5;

/** ผลการค้นหาคนมาเทียบ แสดงกี่แถว */
const FIND_LIMIT = 20;

/**
 * ติ๊กเทียบได้สูงสุดกี่คน
 *
 * ⚠ ต้องตรงกับ MAX_COMPARE ฝั่งเซิร์ฟเวอร์ (services/candidateComparison.ts)
 *   ถ้าฝั่งนี้มากกว่า ผู้ใช้จะติ๊กได้แล้วกดเทียบเจอ 400 ซึ่งเป็นทางตันที่ไม่มีทางออก
 */
const MAX_COMPARE = 3;

/**
 * ⚠ ไม่มีโหมด "กางดูทั้งหมด" แล้ว — ผู้ใช้สั่งเอาปุ่มนั้นออก
 *
 * เดิมชั้นนี้มีสองโหมด คือย่อเหลือห้าคน กับกางดูรายชื่อผู้ผ่านเกณฑ์ทั้งหมด
 * (ซึ่งบางอัตราเป็นพันคน) พร้อมช่องค้นในรายชื่อนั้น
 *
 * ปัญหาของโหมดเต็มคือมันตอบคำถามที่ไม่ค่อยมีใครถาม — ไม่มีใครอ่านรายชื่อพันคน
 * เรียงกันจนจบ สิ่งที่คนทำงานจริงต้องการมีสองอย่าง คือ "ใครอยู่หัวรายชื่อ"
 * กับ "คนที่ฉันมีชื่ออยู่ในใจอยู่แล้ว เขาเข้าเกณฑ์ของอัตรานี้ไหม"
 *
 * ตอนนี้จึงเป็น หัวรายชื่อห้าคน + ช่องค้นหาคนมาเทียบ ซึ่งตอบทั้งสองข้อโดยตรง
 *
 * ⚠ ยอดจริงยังต้องขึ้นที่หัวกล่องเสมอ ("N คน") ห้ามตัดออกเพราะรายการเหลือห้าแถว
 *   ถ้าไม่บอก ผู้ใช้จะอ่านว่าทั้งกระทรวงมีผู้ผ่านเกณฑ์แค่ห้าคน
 */
function EligibleTier({
  pos,
  d,
  tools,
  open,
  onToggle,
  picked,
  onPick,
  onClearPicks,
  onCompare,
}: {
  pos: Position;
  d: VacancyCandidateList;
  /** แถบดาวน์โหลด — เรนเดอร์คั่นระหว่างหัวกล่องกับรายชื่อ (ดูเหตุผลที่จุดที่สร้างมัน) */
  tools: ReactNode;
  open: string | null;
  onToggle: (id: string | null) => void;
  picked: string[];
  /** undefined = หน้าต่างที่ครอบอยู่ไม่รองรับการเทียบ จึงไม่แสดงช่องติ๊กเลย */
  onPick?: (id: string) => void;
  onClearPicks: () => void;
  onCompare?: (ids: string[]) => void;
}) {
  const total = d.eligibleTotals?.total ?? 0;
  const atMax = picked.length >= MAX_COMPARE;
  const firstRows = (d.eligible ?? []).slice(0, FIRST);

  /*
   * ชื่อของคนที่ติ๊กไว้ ใช้เขียนบนชิปในแถบ "เลือกมาเทียบ"
   *
   * ⚠ ต้องเป็น useRef ที่สะสมไปเรื่อย ๆ ไม่ใช่ Map ที่สร้างใหม่ทุกรอบเรนเดอร์
   *
   * ผู้ใช้ติ๊กคนจากผลการค้นหา แล้วพิมพ์คำค้นใหม่เพื่อหาคนที่สอง — ผลการค้นหาชุดเดิม
   * หายไปแล้ว ถ้าสร้างแผนที่ใหม่จากผลปัจจุบัน ชิปของคนแรกจะกลายเป็นรหัสบุคคลดิบ
   * ซึ่งผู้ใช้อ่านไม่ออกว่าเป็นใคร แล้วจะกดเอาออกไม่ถูกตัว
   *
   * ⚠ ห้ามล้างแผนที่นี้ตอนเปลี่ยนคำค้น — มันไม่ได้โตขึ้นเรื่อย ๆ อย่างน่ากังวล
   *   (อย่างมากคือจำนวนแถวที่ผู้ใช้เคยเห็น ซึ่งหลักสิบ) และการล้างคือการทำให้บั๊กข้างบนกลับมา
   */
  const knownNames = useRef(new Map<string, string>()).current;
  for (const c of d.eligible ?? []) if (c.personaId) knownNames.set(c.personaId, c.name);

  const [q, setQ] = useState('');
  const [needle, setNeedle] = useState('');

  /* หน่วงคำค้น ไม่งั้นพิมพ์ชื่อยาวหนึ่งชื่อจะยิงคำขอไปสิบกว่าครั้ง */
  useEffect(() => {
    const t = setTimeout(() => setNeedle(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  /*
   * ค้นหาคนมาเทียบ
   *
   * ⚠ ค้นจาก "คนทั้งหมดที่ครองอัตราอยู่" ไม่ใช่จากรายชื่อผู้ผ่านเกณฑ์
   *
   * เหตุผลคือคำถามที่ช่องนี้ตอบคือ "คนคนนี้เข้าเกณฑ์ของอัตรานี้ไหม" ถ้าค้นได้เฉพาะ
   * คนที่ผ่านเกณฑ์อยู่แล้ว คำถามนั้นจะตอบไม่ได้เลย — คนที่ไม่ผ่านจะหายไปเงียบ ๆ
   * แล้วผู้ใช้จะอ่านว่า "ไม่มีคนชื่อนี้ในระบบ" แทนที่จะเป็น "คนนี้ไม่ผ่านเกณฑ์"
   * ซึ่งคนละเรื่องกันคนละขั้ว
   *
   * ⚠ ต้องกรอง status: 'occupied' — /api/positions ค้นจากชื่อตำแหน่งด้วย
   *   ถ้าไม่กรอง พิมพ์ "นิติกร" จะได้อัตราว่างปนมา ซึ่งไม่มีคนให้เทียบ
   *
   * ⚠ ที่นี่ "ไม่ตัดสิน" ว่าใครผ่านหรือไม่ผ่านเกณฑ์
   *   เรามีรายชื่อผู้ผ่านเกณฑ์อยู่ในมือแค่ส่วนหัว (MAX_PER_TIER คน จากทั้งหมด N คน)
   *   การติดป้าย "ผ่านเกณฑ์" ให้เฉพาะคนที่บังเอิญอยู่ในส่วนหัว จะทำให้คนที่เหลือ
   *   ดูเหมือนไม่ผ่านทั้งที่เราไม่รู้ ตารางเทียบต่างหากที่ตรวจให้จริงเป็นรายข้อ
   */
  const find = useQuery({
    queryKey: ['compare-search', pos.posId, needle],
    enabled: needle.length > 0,
    queryFn: () => api.positions({ q: needle, status: 'occupied', limit: FIND_LIMIT }),
  });

  /* หนึ่งคนอาจครองหลายอัตราในข้อมูลที่ไม่สมบูรณ์ — ตัดซ้ำด้วยรหัสบุคคล
     ไม่งั้นผู้ใช้จะเห็นชื่อเดียวกันสองแถวแล้วไม่รู้ว่าต่างกันตรงไหน */
  const foundPeople: FoundPerson[] = [];
  const seen = new Set<string>();
  for (const it of find.data?.items ?? []) {
    const id = it.incumbent?.personaId;
    /* ⚠ คนที่ไม่มีรหัสบุคคลเทียบไม่ได้ (ตารางเทียบอ้างด้วยรหัส) ต้องไม่ขึ้นในผลค้นหา
       ปุ่มที่กดแล้วไม่เกิดอะไรขึ้นแย่กว่าไม่มีปุ่ม */
    if (!id || !it.incumbent || seen.has(id)) continue;
    seen.add(id);
    knownNames.set(id, it.incumbent.name);
    foundPeople.push({
      id,
      name: it.incumbent.name,
      title: it.title,
      level: it.level,
      unit: it.orgPath[it.orgPath.length - 1] ?? it.unit,
    });
  }

  /* ⚠ กำลังค้นอยู่ = แทนที่รายชื่อห้าอันดับแรก ไม่ใช่ต่อท้ายลงไปอีกก้อน
     ผู้ใช้สั่งว่าส่วนนี้กินเนื้อที่มากเกินไป สองรายการซ้อนกันคือต้นเหตุหลัก
     และการต่อท้ายยังทำให้ชื่อที่ค้นเจอได้ลำดับ 6, 7, 8 ต่อจากคนที่ผ่านเกณฑ์
     ซึ่งอ่านได้ว่า "ระบบจัดให้เขาอยู่อันดับหก" ทั้งที่ระบบไม่ได้จัดอะไรให้เขาเลย */
  const searching = needle.length > 0;

  return (
    <>
      <div className="vc-head">
        {/* ⚠ ช่องค้นอยู่บนสุดของกล่องและกินความกว้างเต็มแถว ตามที่ผู้ใช้สั่ง
            เดิมอยู่แถวเดียวกับหัวข้อ ซึ่งบีบให้เหลือ 260px จนป้ายในช่องถูกตัดกลางคำ */}
        {onPick && (
          <div className="vc-top">
            <span className="vc-findwrap">
              <input
                type="search"
                className="vc-findbox"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                /* ⚠ ป้ายในช่องต้องสั้นพอที่จะอ่านครบในช่องกว้าง 260px
                   วัดแล้วข้อความเดิม ("ค้นหาคนมาเทียบ — ชื่อ ตำแหน่ง ระดับ หน่วยงาน") ถูกตัดกลางคำ
                   รายละเอียดว่าค้นจากอะไรได้บ้างย้ายไปอยู่ใน title กับ aria-label ซึ่งพูดได้เต็ม */
                placeholder="ค้นหาคนมาเทียบ"
                title="ค้นหาจากชื่อคน ชื่อตำแหน่ง ระดับ หรือหน่วยงาน"
                aria-label="ค้นหาคนมาเทียบกับคุณสมบัติของอัตรานี้ — ค้นจากชื่อคน ชื่อตำแหน่ง ระดับ หรือหน่วยงาน"
              />
              {/* ปุ่มล้างของเราเอง ไม่พึ่ง ✕ ที่เบราว์เซอร์แถมมากับ input[type=search]
                  เพราะ Safari ไม่วาดให้เลย ผู้ใช้จึงออกจากโหมดค้นไม่ได้ถ้าไม่ลบทีละตัว */}
              {q && (
                <button
                  type="button"
                  className="vc-findclear"
                  onClick={() => setQ('')}
                  aria-label="ล้างคำค้นและกลับไปดูห้าอันดับแรก"
                >
                  ✕
                </button>
              )}
            </span>
          </div>
        )}

        {/* ⚠ ยอดจริง ("N คน") ห้ามตัดออกเพราะรายการเหลือห้าแถว
            ถ้าไม่บอก ผู้ใช้จะอ่านว่าทั้งกระทรวงมีผู้ผ่านเกณฑ์แค่ห้าคน */}
        <h4>
          ผู้ผ่านเกณฑ์ระยะเวลาตามมาตรฐานกำหนดตำแหน่ง
          <span className="vc-count">{total.toLocaleString('th-TH')} คน</span>
        </h4>

        {/* ⚠ ท่อนหลังของบรรทัดนี้คือคำกำกับว่า "ลำดับที่เห็นแปลว่าอะไร"
            ซึ่งเดิมเป็นย่อหน้าของตัวเองใต้ปุ่มเลือกวิธีเรียง ปุ่มนั้นถูกเอาออกแล้ว
            แต่รายชื่อยังเรียงอยู่และยังมีเลขลำดับ 1-5 กำกับ

            รายชื่อที่เรียงจากบนลงล่างถูกอ่านว่า "คนบนสุดควรได้ตำแหน่ง" เสมอ
            ไม่ว่าหัวข้อจะเขียนว่าอะไร ประโยคนี้จึงห้ามหายไปพร้อมปุ่ม
            ย้ายมาต่อท้ายบรรทัดที่มีอยู่แล้ว จึงไม่กินแถวเพิ่ม */}
        <p className="vc-std">
          ตรวจตาม <b>{d.standardTitle}</b> · เฉพาะเกณฑ์ระยะเวลาการดำรงตำแหน่ง
          {' · เรียงจากผู้ที่มีเวลาเกินเกณฑ์มากที่สุด ไม่ใช่ลำดับความเหมาะสม'}
        </p>

        {/* ---------- เลือกมาเทียบ ----------
            ⚠ อยู่ในหัวกล่องเดียวกับปุ่มเรียงและช่องค้น ไม่ใช่แถบลอยที่ก้นจอ
              กล่องยุบได้ของหน้ารายละเอียดมี overflow: hidden อยู่ (.pdt-fold ใน styles.css)
              position: sticky ข้างในจึงเกาะกับกล่องนั้นแทนที่จะเกาะขอบหน้าต่าง

            ⚠ ตอนยังไม่ได้ติ๊กใคร แถบนี้เป็นข้อความบรรทัดเดียว ไม่ใช่กล่องมีกรอบ
              กล่องเปล่าที่มีแต่คำแนะนำกินความสูงเท่ากล่องที่มีของ ทั้งที่ยังไม่มีอะไรให้ดู */}
        {onPick && (
          <div className={`vc-pickbar${picked.length > 0 ? ' is-on' : ''}`}>
            {picked.length === 0 ? (
              <span className="vc-pickhint">
                ติ๊กช่องหน้าชื่อเพื่อเลือกมาเทียบกัน ได้สูงสุด {MAX_COMPARE} คน
              </span>
            ) : (
              <>
                <span className="vc-pickhint">
                  เลือกไว้ <b>{picked.length}</b> จาก {MAX_COMPARE} คน
                </span>
                <span className="vc-chips">
                  {picked.map((id) => (
                    <button
                      key={id}
                      type="button"
                      className="vc-chip"
                      onClick={() => onPick(id)}
                      aria-label={`เอา ${knownNames.get(id) ?? id} ออกจากรายการที่จะเทียบ`}
                    >
                      {knownNames.get(id) ?? id}
                      <i aria-hidden="true">✕</i>
                    </button>
                  ))}
                </span>
                <span className="vc-picktools">
                  <button type="button" className="vc-clear" onClick={onClearPicks}>
                    ล้างที่เลือก
                  </button>
                  {/* ⚠ คนเดียวกดไม่ได้ และต้องบอกเหตุผลบนปุ่มเอง ไม่ใช่ปุ่มจางที่กดไม่ได้เฉย ๆ
                      เทียบคนเดียวคือหน้าประวัติ ซึ่งกดจากชื่อในรายการได้อยู่แล้ว */}
                  <button
                    type="button"
                    className="vc-cmp"
                    disabled={picked.length < 2 || !onCompare}
                    onClick={() => onCompare?.(picked)}
                  >
                    {picked.length < 2 ? 'เลือกอีกหนึ่งคนจึงจะเทียบได้' : `เทียบ ${picked.length} คน`}
                  </button>
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {tools}

      {searching ? (
        <SearchResults
          needle={needle}
          loading={find.isFetching}
          error={find.error}
          people={foundPeople}
          matched={find.data?.total ?? 0}
          picked={picked}
          atMax={atMax}
          onPick={onPick}
        />
      ) : (
        <>
          <ol className={`vc-list${onPick ? ' is-pickable' : ''}`}>
            {firstRows.map((c, i) => (
              <EligibleRow
                key={c.personaId ?? `${c.name}-${c.seniorityRank}`}
                c={c}
                no={i + 1}
                open={open === c.personaId}
                onToggle={() => onToggle(c.personaId)}
                picked={c.personaId !== null && picked.includes(c.personaId)}
                atMax={atMax}
                onPick={onPick}
                crossLine={d.level.trim() === 'ทรงคุณวุฒิ' && c.currentLevel.trim() === 'อำนวยการสูง'}
              />
            ))}
          </ol>

          {/* ⚠ ต้องบอกว่ารายการนี้ถูกตัดมา ไม่ใช่ปล่อยให้ห้าแถวจบลงเฉย ๆ
              รายการที่จบลงโดยไม่มีอะไรบอก อ่านได้ว่า "มีเท่านี้"
              ⚠ เป็นข้อความ ไม่ใช่ปุ่ม — ปุ่ม "แสดงทั้งหมด" ถูกเอาออกตามที่ผู้ใช้สั่ง */}
          {total > firstRows.length && (
            <p className="vc-cut">
              แสดง {firstRows.length} อันดับแรกจาก {total.toLocaleString('th-TH')} คน
              {onPick ? ' — ใช้ช่องค้นหาด้านบนเพื่อหาคนที่ต้องการ' : ''}
            </p>
          )}
        </>
      )}
    </>
  );
}

interface FoundPerson {
  id: string;
  name: string;
  title: string;
  level: string;
  unit: string;
}

/**
 * ผลการค้นหาคนมาเทียบ — แทนที่รายชื่อห้าอันดับแรกชั่วคราว
 *
 * ⚠ บรรทัดกำกับบรรทัดเดียวด้านบนตัดทิ้งไม่ได้
 *
 * มันคือสิ่งเดียวที่บอกว่าคนที่ค้นเจอ "ยังไม่ได้ถูกตรวจเกณฑ์" ต่างจากรายชื่อที่มันไปแทนที่
 * ซึ่งเป็นผู้ผ่านเกณฑ์จริง ๆ ถ้าไม่มีประโยคนี้ ผู้ใช้จะอ่านว่าทุกคนที่ค้นเจอคือผู้เข้าเงื่อนไข
 * ของอัตรานี้ ทั้งที่ระบบยังไม่ได้ตรวจอะไรให้เขาเลย
 */
function SearchResults({
  needle,
  loading,
  error,
  people,
  matched,
  picked,
  atMax,
  onPick,
}: {
  needle: string;
  loading: boolean;
  error: unknown;
  people: FoundPerson[];
  /** จำนวนอัตราที่ตรงกับคำค้นทั้งหมดที่เซิร์ฟเวอร์นับได้ ก่อนตัดเหลือ FIND_LIMIT */
  matched: number;
  picked: string[];
  atMax: boolean;
  onPick?: (id: string) => void;
}) {
  return (
    <section className="vc-find">
      <p className="vc-findnote">
        ผลการค้นหาจากผู้ครองอัตราทั้งหมด — <b>ยังไม่ได้ตรวจเกณฑ์</b> ตารางเทียบจะตรวจให้เป็นรายข้อ
        {/* ⚠ ต้องบอกว่าผลถูกตัดมา ไม่ใช่ปล่อยให้จบที่แถวสุดท้ายเฉย ๆ
            ผู้ใช้ที่ค้นนามสกุลกว้าง ๆ แล้วไม่เจอคนที่ต้องการ จะสรุปว่า "ไม่มีคนนี้ในระบบ"
            ทั้งที่ความจริงคือเขาอยู่แถวที่ 40 */}
        {matched > people.length && (
          <>
            {' · '}แสดง {people.length} จาก {matched.toLocaleString('th-TH')} — พิมพ์ให้เจาะจงขึ้น
          </>
        )}
      </p>

      {loading && people.length === 0 ? (
        <p className="state sm">กำลังค้นหา…</p>
      ) : error ? (
        <p className="state sm error">{String(error)}</p>
      ) : people.length === 0 ? (
        /* ⚠ "ไม่พบชื่อนี้" ไม่ใช่ "คนนี้ไม่ผ่านเกณฑ์" — ต้องเขียนให้ตรงว่าเป็นอย่างแรก */
        <p className="vc-findnone">ไม่พบผู้ครองอัตราที่ตรงกับ “{needle}”</p>
      ) : (
        <ul className="vc-findlist">
          {people.map((r) => {
            const on = picked.includes(r.id);
            return (
              <li key={r.id}>
                <button
                  type="button"
                  className={`vc-findrow${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  /* ⚠ ครบโควตาแล้วยังต้องกดเอา "คนที่ติ๊กไว้" ออกได้
                     ถ้าปิดทั้งแถว ผู้ใช้ที่ติ๊กครบสามคนจะสลับตัวไม่ได้เลย
                     ต้องไปหาชิปด้านบนกดออกก่อน ซึ่งไม่มีอะไรบอกให้รู้ */
                  disabled={!onPick || (atMax && !on)}
                  onClick={() => onPick?.(r.id)}
                >
                  <span className="vc-findtick" aria-hidden="true">{on ? '✓' : '+'}</span>
                  <span className="vc-findwho">
                    <b>{r.name}</b>
                    <span>
                      {r.title}
                      {r.level && ` · ${levelLabel(r.level)}`}
                      {` · ${r.unit}`}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {atMax && (
        <p className="vc-findnote is-end">
          เลือกครบ {MAX_COMPARE} คนแล้ว — เอาคนใดคนหนึ่งออกก่อนจึงจะเพิ่มคนใหม่ได้
        </p>
      )}
    </section>
  );
}

/* ---------------- แถว ---------------- */

/**
 * แถวที่กดกางประวัติได้
 *
 * ⚠ คนที่ไม่มี personaId กดไม่ได้ และต้องบอกเหตุผลตรงแถวนั้น ไม่ใช่ทำให้กดได้แล้วขึ้นหน้าว่าง
 * ในข้อมูลจริงมีอัตราที่มีผู้ครองแต่ dump ไม่ให้รหัสบุคคลมาด้วย จึงเชื่อมกับตารางประวัติไม่ได้
 * เป็นปัญหาคุณภาพข้อมูลที่ต้องเห็น ไม่ใช่ซ่อน
 */
function Expandable({
  id,
  open,
  onToggle,
  children,
}: {
  id: string | null;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  if (!id) {
    return (
      <>
        <div className="vc-who">{children}</div>
        <span className="vc-noid">ข้อมูลไม่มีรหัสบุคคล จึงเปิดประวัติไม่ได้</span>
      </>
    );
  }

  return (
    <>
      <button type="button" className="vc-open" aria-expanded={open} onClick={onToggle}>
        <span className="vc-who">{children}</span>
        <span className="vc-caret" aria-hidden="true">
          {open ? <DownOutlined /> : <RightOutlined />}
        </span>
      </button>
      {open && (
        <div className="vc-profile">
          <PersonCard personaId={id} embedded />
        </div>
      )}
    </>
  );
}

function EligibleRow({
  c,
  no,
  open,
  onToggle,
  picked,
  atMax,
  onPick,
  crossLine = false,
}: {
  c: EligibilityResult;
  /** ติดป้าย "ข้ามสายงาน" — ผอ. ระดับสูงในรายชื่อของอัตราที่ปรึกษา (ทรงคุณวุฒิ) */
  crossLine?: boolean;
  /** ลำดับที่เท่าไรในรายการนี้ — เปลี่ยนตามวิธีเรียง */
  no: number;
  open: boolean;
  onToggle: () => void;
  picked: boolean;
  /** ติ๊กครบเพดานแล้ว — ช่องของคนที่ยังไม่ได้ติ๊กจะถูกปิด */
  atMax: boolean;
  onPick?: (id: string) => void;
}) {
  return (
    <li className={`${open ? 'is-open' : ''}${picked ? ' is-picked' : ''}`.trim() || undefined}>
      {/* ⚠ คนที่ไม่มีรหัสบุคคลติ๊กไม่ได้ แต่ต้องกินที่ของคอลัมน์ไว้เท่ากัน
          ไม่งั้นชื่อของแถวนั้นจะเลื่อนไปชนเลขลำดับ ดูเหมือนแถวคนละชนิด */}
      {onPick &&
        (c.personaId ? (
          <label className="vc-pick" title={picked ? 'เอาออกจากรายการที่จะเทียบ' : 'เลือกมาเทียบ'}>
            <input
              type="checkbox"
              checked={picked}
              disabled={!picked && atMax}
              onChange={() => onPick(c.personaId!)}
            />
            <span className="sr-only">เลือก {c.name} มาเทียบ</span>
          </label>
        ) : (
          <span className="vc-pick is-off" aria-hidden="true" />
        ))}
      {/* ⚠ เลขในวงกลมคือ "ลำดับในรายการ" ไม่ใช่ลำดับอาวุโส
          ลำดับอาวุโสเป็นเลขที่มีฐานตามหลักเกณฑ์ ก.พ. และไม่เปลี่ยนตามวิธีเรียง
          จึงต้องมีป้ายกำกับของตัวเองอยู่ข้างชื่อ ห้ามเอาสองเลขนี้มาใช้แทนกัน */}
      <span className="vc-rank tnum">{no}</span>
      <Expandable id={c.personaId} open={open} onToggle={onToggle}>
        <span className="rc-hd">
          <b>{c.name}</b>
          {c.passedPaths.length > 0 && (
            <span className="rc-tag is-ok">ผ่านเกณฑ์ข้อ {c.passedPaths.join(', ')}</span>
          )}
          {c.retiresWithinMonths !== null && c.retiresWithinMonths <= 12 && (
            <span className="rc-tag is-warn">เกษียณใน {duration(c.retiresWithinMonths)}</span>
          )}
          {crossLine && <span className="rc-tag is-due">ข้ามสายงาน</span>}
          <span className="rc-tag is-rank">อาวุโสลำดับที่ {c.seniorityRank.toLocaleString('th-TH')}</span>
        </span>
        {/* ⚠ ระดับต้องอยู่คู่ชื่อตำแหน่งเสมอ — ดูเหตุผลที่หัวไฟล์ */}
        <span className="rc-sub">
          {c.currentTitle} · <b>{levelLabel(c.currentLevel)}</b>
        </span>
        <span className="rc-sub">{c.unit}</span>

        <Scores c={c} />
        <Criteria c={c} />

        {c.pendingManualChecks.length > 0 && (
          <span className="vc-manual">
            เงื่อนไขที่ระบบตรวจแทนไม่ได้ ต้องให้เจ้าหน้าที่ยืนยัน:{' '}
            {c.pendingManualChecks.map((m) => m.label).join(' · ')}
          </span>
        )}
      </Expandable>
    </li>
  );
}

/**
 * คะแนนที่มีอยู่ในแฟ้มของคนคนนี้
 *
 * ⚠ นี่คือ "คะแนนของเขา" ไม่ใช่ "คะแนนความเหมาะสมกับตำแหน่งนี้"
 *   ระบบไม่ให้คะแนนความเหมาะสมและไม่จัดอันดับด้วยคะแนน — การให้คะแนนเป็นอำนาจ
 *   ของคณะกรรมการ ลำดับที่แสดงยังเป็นลำดับอาวุโสตามหลักเกณฑ์ ก.พ. เหมือนเดิม
 *   ห้ามเอาตัวเลขพวกนี้ไปเรียงลำดับรายชื่อ และห้ามรวมเป็นคะแนนเดียว
 *   เพราะการรวมคือการตั้งน้ำหนักให้แต่ละด้าน ซึ่งไม่มีเอกสารรองรับ
 */
function Scores({ c }: { c: EligibilityResult }) {
  const perf = c.latestPerformance;
  if (!perf && c.competencyAvg === null) return null;

  return (
    <span className="rc-scores">
      {perf && (
        <span className="rc-score">
          <em>ผลประเมิน {perf.year ?? '—'}</em>
          <b>{perf.score === null ? (perf.rating ?? '—') : perf.score.toFixed(1)}</b>
          {perf.score !== null && perf.rating && <i>{perf.rating}</i>}
        </span>
      )}
      {c.competencyAvg !== null && (
        <span className="rc-score">
          <em>สมรรถนะเฉลี่ย</em>
          <b>{c.competencyAvg.toFixed(1)}</b>
          <i>จาก 5 · {c.competencyCount} ด้าน</i>
        </span>
      )}
    </span>
  );
}

/**
 * เข้าเกณฑ์ด้วยข้อไหน และห่างจากเกณฑ์เท่าไร
 *
 * ⚠ แสดงข้อที่ "ไม่ผ่าน" ด้วย ไม่ใช่เฉพาะข้อที่ผ่าน
 *   ข้อที่ไม่ผ่านคือสิ่งที่บอกว่าเขาขาดอะไรอยู่ ซึ่งเป็นข้อมูลที่ใช้ตัดสินใจได้จริง
 *   ถ้าโชว์แต่ข้อที่ผ่าน ทุกคนในรายชื่อจะดูเหมือนกันหมดและเทียบกันไม่ได้เลย
 */
function Criteria({ c }: { c: EligibilityResult }) {
  if (c.paths.length === 0) return null;
  const passed = c.paths.filter((x) => x.passed).length;

  return (
    <span className="rc-crit">
      <span className="rc-crit-h">
        เข้าเกณฑ์ <b>{passed}</b> จาก {c.paths.length} ข้อ
        {/* ⚠ ส่วนเกินคือตัวที่ใช้เรียงลำดับ ต้องเห็นได้ ไม่งั้นลำดับจะดูเหมือนสุ่ม
            "วัดไม่ได้" ไม่ใช่ศูนย์ — ผ่านเฉพาะข้อที่ไม่กำหนดระยะเวลา */}
        {' · '}
        {c.surplusMonths === null ? (
          <em>ข้อที่ผ่านไม่กำหนดระยะเวลา จึงวัดส่วนเกินไม่ได้</em>
        ) : (
          <>เกินเกณฑ์ <b>{duration(c.surplusMonths)}</b></>
        )}
      </span>
      <span className="rc-crit-list">
        {c.paths.map((x) => (
          <span key={x.id} className={`rc-crit-row${x.passed ? ' is-ok' : ''}`}>
            <i aria-hidden="true">{x.passed ? '✓' : '·'}</i>
            <span className="t">
              <b>ข้อ {x.id}</b> {x.label}
            </span>
            <span className="n tnum">
              {x.monthsRequired === null
                ? 'ไม่กำหนดระยะเวลา'
                : `${duration(x.monthsCounted)} / ${duration(x.monthsRequired)}`}
            </span>
          </span>
        ))}
      </span>
    </span>
  );
}

/* ================================================================
   กลุ่มแนวราบ — 5 อันดับแรก แล้วค้นหาคนที่เหลือเอง
   ผู้ใช้สั่ง 19 ก.ย. 2569: "ทุกตำแหน่ง ให้แสดง 5 ลำดับแรกที่เข้าเงื่อนไขมากที่สุดเสมอ นอกนั้นให้ค้นหาเอา"
   ⚠ ช่องค้นนี้ค้น "เฉพาะในกลุ่มแนวราบ" ที่เซิร์ฟเวอร์ (/candidates/lateral) ไม่ใช่เฉพาะแถวที่โหลดมาแล้ว
     ไม่งั้นค้นชื่อคนที่อยู่แถวที่ 30 จะได้ "ไม่พบ" ซึ่งอ่านว่าเขาย้ายมาไม่ได้
   ================================================================ */
function LateralTier({
  posId,
  d,
  open,
  onToggle,
  heading = false,
  note,
}: {
  posId: string;
  d: VacancyCandidateList;
  open: string | null;
  onToggle: (id: string | null) => void;
  /** true = เป็นรายชื่อหลักของอัตรา — แสดงหัวข้อพร้อมยอด */
  heading?: boolean;
  note?: ReactNode;
}) {
  const [q, setQ] = useState('');
  const [needle, setNeedle] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setNeedle(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const find = useQuery({
    queryKey: ['lateral-search', posId, needle],
    enabled: needle.length > 0,
    queryFn: () => api.lateralCandidates(posId, { q: needle, limit: FIND_LIMIT }),
  });
  const total = d.lateralTotals.found;
  const searching = needle.length > 0;
  const rows = searching ? (find.data?.items ?? []) : d.lateral.slice(0, FIRST);

  return (
    <>
      <div className="vc-head">
        {total > FIRST && (
          <div className="vc-top">
            <span className="vc-findwrap">
              <input
                type="search"
                className="vc-findbox"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ค้นหาในรายชื่อนี้"
                title="ค้นหาจากชื่อคน ชื่อตำแหน่ง หรือหน่วยงาน"
                aria-label="ค้นหาในรายชื่อผู้ที่ย้ายแนวราบมาได้ — ค้นจากชื่อคน ชื่อตำแหน่ง หรือหน่วยงาน"
              />
              {q && (
                <button type="button" className="vc-findclear" onClick={() => setQ('')} aria-label="ล้างคำค้นและกลับไปดูห้าอันดับแรก">
                  ✕
                </button>
              )}
            </span>
          </div>
        )}
        {heading && (
          <h4>
            ผู้ดำรงตำแหน่งในสายงานและระดับเดียวกัน ณ ส่วนราชการอื่น
            <span className="vc-count">{total.toLocaleString('th-TH')} คน</span>
          </h4>
        )}
        {note && <p className="vc-std">{note}</p>}
        <p className="vc-std">
          ย้ายมาดำรงตำแหน่งนี้ได้โดยไม่ต้องเลื่อนระดับ · เรียงจากคนในส่วนราชการเดียวกันก่อน
          แล้วดูระยะเวลาครองตำแหน่งเดิม ไม่ใช่ลำดับความเหมาะสม
        </p>
      </div>

      {searching && find.isFetching && rows.length === 0 ? (
        <p className="state sm">กำลังค้นหา…</p>
      ) : searching && find.error ? (
        <p className="state sm error">{String(find.error)}</p>
      ) : rows.length === 0 ? (
        <p className="pmodal-none">
          {searching
            ? `ไม่พบ “${needle}” ในรายชื่อนี้`
            : `ไม่มีใครอยู่สายงาน “${d.line || '—'}” ระดับ${levelLabel(d.level) || '—'} ในหน่วยอื่น`}
        </p>
      ) : (
        <ol className="vc-list">
          {rows.map((c, i) => (
            <LateralRow
              key={`${c.personaId ?? c.name}-${i}`}
              c={c}
              open={open === c.personaId}
              onToggle={() => onToggle(c.personaId)}
            />
          ))}
        </ol>
      )}

      {!searching && total > rows.length && (
        <p className="vc-cut">
          แสดง {rows.length} อันดับแรกจาก {total.toLocaleString('th-TH')} คน — ใช้ช่องค้นหาด้านบนเพื่อหาคนที่ต้องการ
        </p>
      )}
      {searching && (find.data?.total ?? 0) > rows.length && (
        <p className="vc-cut">
          แสดง {rows.length} จาก {(find.data?.total ?? 0).toLocaleString('th-TH')} คนที่ตรงคำค้น — พิมพ์ให้เจาะจงขึ้น
        </p>
      )}
    </>
  );
}

function LateralRow({
  c,
  open,
  onToggle,
}: {
  c: VacancyCandidate;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <li className={open ? 'is-open' : undefined}>
      <Expandable id={c.personaId} open={open} onToggle={onToggle}>
        <span className="rc-hd">
          <b>{c.name}</b>
          {c.sameDepartment && <span className="rc-tag is-ok">ส่วนราชการเดียวกัน</span>}
          {c.longTenure && <span className="rc-tag is-due">ครองตำแหน่งเดิมนาน</span>}
          {c.retireInYears !== null && c.retireInYears <= 1 && (
            <span className="rc-tag is-warn">เกษียณภายใน 1 ปี</span>
          )}
        </span>
        <span className="rc-sub">
          {c.title}
          {c.posNo && ` · เลขที่ ${c.posNo}`}
        </span>
        <span className="rc-sub">{c.orgPath.length > 0 ? c.orgPath.join(' › ') : c.unit}</span>
        <span className="rc-sub">
          {c.monthsInPosition === null
            ? 'ไม่มีวันครองตำแหน่งในข้อมูล'
            : `ครองตำแหน่งปัจจุบันมาแล้ว ${duration(c.monthsInPosition)}`}
          {` · หน่วยต้นทางมีคนครอง ${Math.round(c.fromUnitRatio * 100)}%`}
        </span>
      </Expandable>
    </li>
  );
}

function duration(months: number): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (!y) return `${m} เดือน`;
  return m ? `${y} ปี ${m} เดือน` : `${y} ปี`;
}
